import type { WalletRecipientView } from '@/api/address-book-bff/types';
import CopyIcon from '@/assets/svg/ui-elements/copy-filled-current.svg';
import ClearIcon from '@/assets/svg/ui-elements/x.svg';
import { theme, Tooltip } from '@/design-system';
import { useCopy } from '@/global-hooks/use-copy';
import { truncateAddress } from '@/shared/modules/wallet/send/recipient';

import { getRecipientLabel } from './recipient-name';
import { SendRecipientAvatar } from './send-recipient-avatar';
import {
  ActionButton,
  Actions,
  SelectedAddress,
  SelectedContent,
  SelectedField,
  SelectedIdentity,
  SelectedIdentityButton,
  SelectedInput,
  SelectedName,
} from './send-recipient-combobox.styled';

export interface SendRecipientSelectedProps {
  inputId: string;
  helperId: string;
  selectedRecipient: WalletRecipientView;
  invalid: boolean;
  helperText: string;
  disabled: boolean;
  onEdit(): void;
  onClear(): void;
}

export const SendRecipientSelected = ({
  inputId,
  helperId,
  selectedRecipient,
  invalid,
  helperText,
  disabled,
  onEdit,
  onClear,
}: SendRecipientSelectedProps) => {
  const selectedAddress = selectedRecipient.wallet.address;
  const { isTooltipOpen, handleClick: handleCopy } = useCopy({ textToCopy: selectedAddress });

  return (
    <SelectedField
      role="group"
      aria-label="Selected recipient"
      data-slot="send-recipient-field"
      $invalid={invalid}
      $disabled={disabled}
    >
      <SelectedInput
        id={inputId}
        name="recipient"
        readOnly
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={helperText ? helperId : undefined}
        placeholder="Enter Sui address, name or email"
        autoComplete="off"
        spellCheck={false}
      />
      <SelectedContent>
        <SelectedIdentityButton
          type="button"
          aria-label="Edit recipient"
          disabled={disabled}
          onClick={onEdit}
        >
          <SendRecipientAvatar recipient={selectedRecipient} />
          <SelectedIdentity>
            <SelectedName
              as="span"
              variant="body3"
              fontWeight="medium"
              color={theme.colors.text.primary}
              noWrap
            >
              {getRecipientLabel(selectedRecipient)}
            </SelectedName>
            <SelectedAddress as="span" variant="body4" color={theme.colors.text.tertiary} noWrap>
              {truncateAddress(selectedAddress)}
            </SelectedAddress>
          </SelectedIdentity>
        </SelectedIdentityButton>
        <Tooltip title="Copied" open={isTooltipOpen} disabled={disabled} side="top">
          <ActionButton
            type="button"
            size="xs"
            variant="ghost"
            color="secondary"
            aria-label="Copy recipient address"
            disabled={disabled}
            onClick={handleCopy}
          >
            <CopyIcon aria-hidden="true" />
          </ActionButton>
        </Tooltip>
      </SelectedContent>
      <Actions>
        <ActionButton
          type="button"
          size="xs"
          variant="ghost"
          color="secondary"
          aria-label="Clear recipient"
          disabled={disabled}
          onClick={onClear}
        >
          <ClearIcon />
        </ActionButton>
      </Actions>
    </SelectedField>
  );
};
